import {
    type CSSProperties,
    type ReactNode,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    formatDateTime,
    formatNumber,
    ToggleGroup,
    AreaChart,
    Chart
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import type {
    IAreaChartProps,
    ChartDataRow,
    ChartConfig
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Callout,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "AreaChart";

const USAGE_CODE = `<Chart
    config={{
        visitors: {
            title: "Visitors",
            colorIndex: 1
        }
    }}
    description="Monthly unique visitors, last 12 months"
    note="Nov 2025 – Oct 2026"
    title="Visitors"
>
    <Chart.Value
        description="vs. last month"
        value="43,215"
        delta={0.125}
    />
    <AreaChart
        categoryKey="month"
        fill="gradient"
        curve="natural"
        data={data}
    />
</Chart>`;

const CURVE_CODE = `<AreaChart
    categoryKey="day"
    curve="linear"
    fill="solid"
    data={data}
/>

<AreaChart
    categoryKey="hour"
    fill="gradient"
    curve="step"
    data={data}
/>`;

const STACK_CODE = `<Chart
    isLegendToggleable={true}
    title="Visitors by device"
    config={config}
>
    <AreaChart
        stackBehaviour="stacked"
        categoryKey="month"
        data={data}
    />
</Chart>`;

const EXPANDED_CODE = `<AreaChart
    stackBehaviour="expanded"
    categoryKey="month"
    data={data}
/>`;

const GAPS_CODE = `const data = [
    {
        mobile: 1980,
        desktop: 780,
        day: 4
    },
    {
        desktop: 770,
        mobile: null,
        day: 5
    }
];`;

const RANGE_CODE = `const [range, setRange] = useState("90");

<Chart
    isLegendToggleable={true}
    title="Visitors"
    config={config}
>
    <Chart.Range
        options={[
            {
                title: "7 days",
                key: "7"
            },
            {
                title: "30 days",
                key: "30"
            },
            {
                title: "3 months",
                key: "90"
            }
        ]}
        onChange={setRange}
        value={range}
    />
    <AreaChart
        data={rows.slice(-Number(range))}
        categoryKey="day"
    />
</Chart>`;

const CURVES = [
    "natural",
    "linear",
    "step"
] as const;

const FILLS = [
    "gradient",
    "solid",
    "none"
] as const;

const STACKS = [
    "overlay",
    "stacked",
    "expanded"
] as const;

const RANGES = [
    "7",
    "30",
    "90"
] as const;

const VISITORS = [
    31240,
    34810,
    28950,
    27430,
    30120,
    32680,
    35940,
    33270,
    29860,
    31540,
    38410,
    43215
];

const SHARES = [
    [
        46,
        28,
        18
    ],
    [
        43,
        27,
        19
    ],
    [
        47,
        29,
        17
    ],
    [
        48,
        29,
        16
    ],
    [
        47,
        28,
        18
    ],
    [
        46,
        28,
        19
    ],
    [
        45,
        27,
        21
    ],
    [
        44,
        27,
        22
    ],
    [
        42,
        26,
        25
    ],
    [
        41,
        26,
        26
    ],
    [
        44,
        28,
        21
    ],
    [
        43,
        27,
        20
    ]
];

const DESKTOP_MONTHS = [
    14020,
    12310,
    10150,
    10720,
    15360,
    16850
];

const MOBILE_MONTHS = [
    21920,
    20960,
    19710,
    20820,
    23050,
    26365
];

const HOURS = [
    412,
    268,
    171,
    118,
    96,
    124,
    238,
    486,
    812,
    1064,
    1182,
    1236,
    1318,
    1274,
    1205,
    1168,
    1142,
    1096,
    1024,
    1087,
    1196,
    1248,
    984,
    671
];

const DESKTOP_WEEK = [
    0.62,
    1.04,
    1.06,
    1.05,
    1,
    0.92,
    0.58
];

const MOBILE_WEEK = [
    1.18,
    0.95,
    0.94,
    0.95,
    0.97,
    1,
    1.12
];

const DAY_COUNT = 90;

const FIRST_DAY = new Date(2026, 7, 2);

const VISITORS_KEYS = [
    "visitors"
];

const SESSIONS_KEYS = [
    "sessions"
];

const VIEWS_KEYS = [
    "views"
];

type GapRowType = IAreaChartProps["data"][number];

type CurveType = (typeof CURVES)[number];

type FillType = (typeof FILLS)[number];

type StackType = (typeof STACKS)[number];

type RangeType = (typeof RANGES)[number];

type DayRow = {
    sessions: number;
    desktop: number;
    mobile: number;
    day: number;
};

const getWave = (day: number, salt: number) => {
    const raw = Math.sin(day * 12.9898 + salt * 78.233) * 43758.5453;

    return raw - Math.floor(raw) - 0.5;
};

const DAYS: Array<DayRow> = Array.from({
    length: DAY_COUNT
}, (_, day) => {
    const weekday = new Date(FIRST_DAY.getFullYear(), FIRST_DAY.getMonth(), FIRST_DAY.getDate() + day).getDay();

    const progress = day / (DAY_COUNT - 1);

    const desktop = Math.round((640 + 330 * progress) * (DESKTOP_WEEK[weekday] ?? 1) * (1 + getWave(day, 1) * 0.12));

    const mobile = Math.round((1060 + 400 * progress) * (MOBILE_WEEK[weekday] ?? 1) * (1 + getWave(day, 2) * 0.12));

    return {
        sessions: Math.round(desktop * 3.4 + mobile * 0.9),
        desktop,
        mobile,
        day
    };
});

const SOURCE_KEYS = [
    "organic",
    "direct",
    "social",
    "email"
];

const DEVICE_KEYS = [
    "desktop",
    "mobile"
];

const getVisitorRows = (): Array<ChartDataRow> => VISITORS.map((visitors, index) => ({
    month: index,
    visitors
}));

const getDeviceRows = (): Array<ChartDataRow> => DESKTOP_MONTHS.map((desktop, index) => ({
    mobile: MOBILE_MONTHS[index] ?? 0,
    month: index + 4,
    desktop
}));

const getSourceRows = (): Array<ChartDataRow> => SHARES.map((share, index) => {
    const [
        organic = 0,
        direct = 0,
        social = 0
    ] = share;

    const total = VISITORS[index] ?? 0;

    const organicCount = Math.round(total * organic / 100);

    const directCount = Math.round(total * direct / 100);

    const socialCount = Math.round(total * social / 100);

    return {
        email: total - organicCount - directCount - socialCount,
        organic: organicCount,
        direct: directCount,
        social: socialCount,
        month: index
    };
});

const getSessionRows = (): Array<ChartDataRow> => DAYS.slice(-14).map((row) => ({
    sessions: row.sessions,
    day: row.day
}));

const getHourRows = (): Array<ChartDataRow> => HOURS.map((views, hour) => ({
    views,
    hour
}));

const getGapRows = (): Array<GapRowType> => DAYS.slice(-14).map((row, index) => ({
    mobile: index === 5 || index === 6 ? null : row.mobile,
    desktop: row.desktop,
    day: row.day
}));

const getRangeRows = (range: RangeType): Array<ChartDataRow> => DAYS.slice(-Number(range)).map((row) => ({
    desktop: row.desktop,
    mobile: row.mobile,
    day: row.day
}));

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("areaChartPage.curveTitle"),
        id: "curve"
    },
    {
        title: t("areaChartPage.stackTitle"),
        id: "stack"
    },
    {
        title: t("areaChartPage.expandedTitle"),
        id: "expanded"
    },
    {
        title: t("areaChartPage.gapsTitle"),
        id: "gaps"
    },
    {
        title: t("areaChartPage.rangeTitle"),
        id: "range"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const AreaChartPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        shapeConfigs
    } = NCoreUIKitTheme.useContext();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const [
        curve,
        setCurve
    ] = useState<CurveType>("natural");

    const [
        fill,
        setFill
    ] = useState<FillType>("gradient");

    const [
        stack,
        setStack
    ] = useState<StackType>("stacked");

    const [
        range,
        setRange
    ] = useState<RangeType>("90");

    const chartStyle: CSSProperties = {
        maxWidth: shapeConfigs.contentMaxWidth / 1.5,
        width: "100%"
    };

    const rowStyle: CSSProperties = {
        justifyContent: "center",
        display: "flex",
        width: "100%"
    };

    const getMonth = (value: number | string | null | undefined) => formatDateTime({
        date: new Date(2025, 10 + Number(value), 1),
        locale: activeLocale,
        options: {
            month: "short"
        }
    });

    const getDay = (value: number | string | null | undefined) => formatDateTime({
        date: new Date(FIRST_DAY.getFullYear(), FIRST_DAY.getMonth(), FIRST_DAY.getDate() + Number(value)),
        locale: activeLocale,
        options: {
            month: "short",
            day: "numeric"
        }
    });

    const getHour = (value: number | string | null | undefined) => `${String(value).padStart(2, "0")}:00`;

    const getCount = (value: number) => formatNumber({
        options: {
            maximumFractionDigits: 0
        },
        locale: activeLocale,
        value
    });

    const visitorsConfig: ChartConfig = {
        visitors: {
            title: t("areaChartPage.visitorsSeries"),
            colorIndex: 1
        }
    };

    const sessionsConfig: ChartConfig = {
        sessions: {
            title: t("areaChartPage.sessionsSeries"),
            colorIndex: 1
        }
    };

    const viewsConfig: ChartConfig = {
        views: {
            title: t("areaChartPage.viewsSeries"),
            colorIndex: 1
        }
    };

    const deviceConfig: ChartConfig = {
        desktop: {
            title: t("areaChartPage.desktop"),
            colorIndex: 1
        },
        mobile: {
            title: t("areaChartPage.mobile"),
            colorIndex: 2
        }
    };

    const sourceConfig: ChartConfig = {
        organic: {
            title: t("areaChartPage.organic"),
            colorIndex: 1
        },
        direct: {
            title: t("areaChartPage.direct"),
            colorIndex: 2
        },
        social: {
            title: t("areaChartPage.social"),
            colorIndex: 3
        },
        email: {
            title: t("areaChartPage.email"),
            colorIndex: 4
        }
    };

    const pick = <ItemType extends string>(items: ReadonlyArray<ItemType>, setter: (item: ItemType) => void) => (value: string | Array<string>) => {
        const nextItem = items.find((item) => item === value);

        if(nextItem) {
            setter(nextItem);
        }
    };

    const renderToggle = <ItemType extends string>(title: string, items: ReadonlyArray<ItemType>, value: ItemType, setter: (item: ItemType) => void) => <ToggleGroup
        onValueChange={pick(items, setter)}
        attachBehaviour="attached"
        isClearEnabled={false}
        variant="outline"
        title={title}
        value={value}
        size="small"
    >
        {
            items.map((item) => <ToggleGroup.Item
                title={item}
                value={item}
                key={item}
            />)
        }
    </ToggleGroup>;

    const renderChart = ({
        isLegendToggleable,
        description,
        children,
        config,
        title,
        note
    }: {
        isLegendToggleable?: boolean;
        children: Array<ReactNode>;
        config: ChartConfig;
        description: string;
        title: string;
        note?: string;
    }) => <div
        style={rowStyle}
    >
        <div
            style={chartStyle}
        >
            <Chart
                isLegendToggleable={isLegendToggleable}
                spreadBehaviour="stretch"
                description={description}
                config={config}
                title={title}
                note={note}
            >
                {children}
            </Chart>
        </div>
    </div>;

    return <Article>
        <ComponentHeader
            lead={t("areaChartPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("areaChartPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("areaChartPage.overviewCaption")}
            >
                {
                    renderChart({
                        description: t("areaChartPage.visitorsDescription"),
                        children: [
                            <Chart.Value
                                description={t("areaChartPage.heroPeriod")}
                                value={getCount(VISITORS[11] ?? 0)}
                                delta={0.125}
                                key="value"
                            />,
                            <AreaChart
                                categoryTitle={t("areaChartPage.month")}
                                categoryFormatter={getMonth}
                                seriesKeys={VISITORS_KEYS}
                                valueFormatter={getCount}
                                data={getVisitorRows()}
                                categoryKey="month"
                                key="chart"
                            />
                        ],
                        title: t("areaChartPage.visitorsTitle"),
                        note: t("areaChartPage.visitorsNote"),
                        config: visitorsConfig
                    })
                }
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("areaChartPage.curveTitle")}
            id="curve"
        >
            <Paragraph>
                {t("areaChartPage.curveText")}
            </Paragraph>
            <Preview
                caption={t("areaChartPage.curveCaption")}
            >
                {renderToggle("curve", CURVES, curve, setCurve)}
                {renderToggle("fill", FILLS, fill, setFill)}
                {
                    renderChart({
                        description: t("areaChartPage.sessionsDescription"),
                        children: [
                            <AreaChart
                                categoryTitle={t("areaChartPage.day")}
                                seriesKeys={SESSIONS_KEYS}
                                categoryFormatter={getDay}
                                valueFormatter={getCount}
                                data={getSessionRows()}
                                categoryKey="day"
                                plotSize="small"
                                curve={curve}
                                fill={fill}
                                key="chart"
                            />
                        ],
                        title: t("areaChartPage.sessionsTitle"),
                        note: t("areaChartPage.sessionsNote"),
                        config: sessionsConfig
                    })
                }
            </Preview>
            <Paragraph>
                {t("areaChartPage.stepText")}
            </Paragraph>
            <Preview
                caption={t("areaChartPage.stepCaption")}
            >
                {
                    renderChart({
                        description: t("areaChartPage.viewsDescription"),
                        children: [
                            <AreaChart
                                categoryTitle={t("areaChartPage.hour")}
                                categoryFormatter={getHour}
                                valueFormatter={getCount}
                                seriesKeys={VIEWS_KEYS}
                                data={getHourRows()}
                                categoryKey="hour"
                                plotSize="small"
                                curve="step"
                                key="chart"
                            />
                        ],
                        title: t("areaChartPage.viewsTitle"),
                        config: viewsConfig
                    })
                }
            </Preview>
            <CodeBlock
                code={CURVE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("areaChartPage.stackTitle")}
            id="stack"
        >
            <Paragraph>
                {t("areaChartPage.stackText")}
            </Paragraph>
            <Preview
                caption={t("areaChartPage.stackCaption")}
            >
                {renderToggle("stackBehaviour", STACKS, stack, setStack)}
                {
                    renderChart({
                        description: t("areaChartPage.deviceDescription"),
                        children: [
                            <AreaChart
                                categoryTitle={t("areaChartPage.month")}
                                categoryFormatter={getMonth}
                                valueFormatter={getCount}
                                seriesKeys={DEVICE_KEYS}
                                data={getDeviceRows()}
                                stackBehaviour={stack}
                                categoryKey="month"
                                key="chart"
                            />
                        ],
                        title: t("areaChartPage.deviceTitle"),
                        note: t("areaChartPage.deviceNote"),
                        isLegendToggleable: true,
                        config: deviceConfig
                    })
                }
            </Preview>
            <CodeBlock
                code={STACK_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("areaChartPage.expandedTitle")}
            id="expanded"
        >
            <Paragraph>
                {t("areaChartPage.expandedText")}
            </Paragraph>
            <Preview
                caption={t("areaChartPage.expandedCaption")}
            >
                {
                    renderChart({
                        description: t("areaChartPage.sourceDescription"),
                        children: [
                            <AreaChart
                                categoryTitle={t("areaChartPage.month")}
                                categoryFormatter={getMonth}
                                stackBehaviour="expanded"
                                seriesKeys={SOURCE_KEYS}
                                data={getSourceRows()}
                                categoryKey="month"
                                key="chart"
                            />
                        ],
                        note: t("areaChartPage.visitorsNote"),
                        title: t("areaChartPage.sourceTitle"),
                        isLegendToggleable: true,
                        config: sourceConfig
                    })
                }
            </Preview>
            <CodeBlock
                code={EXPANDED_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("areaChartPage.gapsTitle")}
            id="gaps"
        >
            <Paragraph>
                {t("areaChartPage.gapsText")}
            </Paragraph>
            <Preview
                caption={t("areaChartPage.gapsCaption")}
            >
                {
                    renderChart({
                        description: t("areaChartPage.gapsDescription"),
                        children: [
                            <AreaChart
                                categoryTitle={t("areaChartPage.day")}
                                categoryFormatter={getDay}
                                valueFormatter={getCount}
                                seriesKeys={DEVICE_KEYS}
                                data={getGapRows()}
                                categoryKey="day"
                                plotSize="small"
                                key="chart"
                            />
                        ],
                        title: t("areaChartPage.gapsChartTitle"),
                        isLegendToggleable: true,
                        config: deviceConfig
                    })
                }
            </Preview>
            <CodeBlock
                code={GAPS_CODE}
                language="tsx"
            />
            <Callout>
                {t("areaChartPage.gapsNote")}
            </Callout>
        </Section>
        <Section
            title={t("areaChartPage.rangeTitle")}
            id="range"
        >
            <Paragraph>
                {t("areaChartPage.rangeText")}
            </Paragraph>
            <Preview
                caption={t("areaChartPage.rangeCaption")}
            >
                {
                    renderChart({
                        description: t("areaChartPage.rangeChartDescription"),
                        children: [
                            <Chart.Range
                                options={RANGES.map((key) => ({
                                    title: t(`areaChartPage.range.${key}`),
                                    key
                                }))}
                                title={t("areaChartPage.rangeLabel")}
                                onChange={pick(RANGES, setRange)}
                                value={range}
                                key="range"
                            />,
                            <AreaChart
                                categoryTitle={t("areaChartPage.day")}
                                categoryFormatter={getDay}
                                data={getRangeRows(range)}
                                valueFormatter={getCount}
                                seriesKeys={DEVICE_KEYS}
                                categoryKey="day"
                                key="chart"
                            />
                        ],
                        title: t("areaChartPage.visitorsTitle"),
                        isLegendToggleable: true,
                        config: deviceConfig
                    })
                }
            </Preview>
            <CodeBlock
                code={RANGE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("areaChartPage.a11yPlot"),
                    t("areaChartPage.a11yKeyboard"),
                    t("areaChartPage.a11yLive"),
                    t("areaChartPage.a11yHidden"),
                    t("areaChartPage.a11yTable"),
                    t("areaChartPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        type: "Array<Record<string, number | string | null | undefined>>",
                        description: t("areaChartPage.propData"),
                        name: "data"
                    },
                    {
                        description: t("areaChartPage.propCategoryKey"),
                        name: "categoryKey",
                        type: "string"
                    },
                    {
                        description: t("areaChartPage.propSeriesKeys"),
                        type: "Array<string>",
                        name: "seriesKeys"
                    },
                    {
                        type: "\"natural\" | \"linear\" | \"step\"",
                        description: t("areaChartPage.propCurve"),
                        defaultValue: "\"natural\"",
                        name: "curve"
                    },
                    {
                        type: "\"gradient\" | \"solid\" | \"none\"",
                        description: t("areaChartPage.propFill"),
                        defaultValue: "\"gradient\"",
                        name: "fill"
                    },
                    {
                        description: t("areaChartPage.propStackBehaviour"),
                        type: "\"overlay\" | \"stacked\" | \"expanded\"",
                        defaultValue: "\"overlay\"",
                        name: "stackBehaviour"
                    },
                    {
                        description: t("areaChartPage.propValueFormatter"),
                        type: "(value: number) => string",
                        name: "valueFormatter"
                    },
                    {
                        type: "(value: number | string, index: number) => string",
                        description: t("areaChartPage.propCategoryFormatter"),
                        name: "categoryFormatter"
                    },
                    {
                        description: t("areaChartPage.propCategoryTitle"),
                        name: "categoryTitle",
                        type: "string"
                    },
                    {
                        description: t("areaChartPage.propPlotSize"),
                        type: "\"small\" | \"medium\" | \"large\"",
                        defaultValue: "\"medium\"",
                        name: "plotSize"
                    },
                    {
                        description: t("areaChartPage.propIsAnimatedOnMount"),
                        name: "isAnimatedOnMount",
                        type: "boolean"
                    },
                    {
                        description: t("areaChartPage.propTooltipBehaviour"),
                        type: "\"auto\" | \"follow\" | \"pinned\"",
                        name: "tooltipBehaviour",
                        defaultValue: "\"auto\""
                    },
                    {
                        type: "(content: ChartTooltipContent) => ReactNode",
                        description: t("areaChartPage.propRenderTooltip"),
                        name: "renderTooltip"
                    },
                    {
                        description: t("areaChartPage.propOnActiveChange"),
                        type: "(index: number | undefined) => void",
                        name: "onActiveChange"
                    },
                    {
                        description: t("areaChartPage.propSummary"),
                        name: "summary",
                        type: "string"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("areaChartPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("areaChartPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("areaChartPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default AreaChartPage;
